import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { LeagueService } from 'src/app/services/v2/league.service';
import { LeagueModel } from 'src/app/shared/models/league-model';
import { isSuccessResponse } from 'src/app/shared/models/api-response.model';

interface CacheEntry {
    league: LeagueModel | null;
    ts: number;
}

/**
 * Resolves a league by its URL code with a short in-memory cache, shared by the
 * `publicViewV3Guard` (`CanMatch`, re-runs on every navigation) and the
 * league-v3 shell — so a `glass` league is fetched once per navigation burst,
 * not on every tab switch. `LeagueService.getLeagueByCode` is itself uncached.
 */
@Injectable({ providedIn: 'root' })
export class LeaguePublicResolverService {
    private readonly leagueService = inject(LeagueService);
    private readonly cache = new Map<string, CacheEntry>();
    private readonly ttlMs = 60_000;

    resolveByCode(code: string): Observable<LeagueModel | null> {
        const hit = this.cache.get(code);
        if (hit && Date.now() - hit.ts < this.ttlMs) {
            return of(hit.league);
        }

        const request$ = this.leagueService.getLeagueByCode(code) ?? of(null);
        return request$.pipe(
            map(response => (response && isSuccessResponse(response) && response.data ? response.data : null)),
            catchError(() => of(null)),
            tap(league => this.cache.set(code, { league, ts: Date.now() }))
        );
    }

    invalidate(code?: string): void {
        if (code) {
            this.cache.delete(code);
        } else {
            this.cache.clear();
        }
    }
}
